import { AssistantMessageToolbarButton } from "#features/conversations/components/conversation-view/AssistantMessageToolbarButton.tsx";
import { useSharedTranslation } from "#hooks/useSharedTranslation.ts";
import { useTracking } from "#hooks/useTracking.ts";
import { MixpanelCopyResponseType, MixpanelEvent } from "#types/tracking.ts";
import type { ConversationMessage } from "#types/types.ts";
import { IconCheck, IconDuplicate, IconX } from "@intentui/icons";
import { useCallback, useMemo, useState, type ReactNode } from "react";

interface Props {
    conversationId: string;
    message: ConversationMessage;
}

type CopyStatus = "success" | "error" | "none";

interface CopyStatusContent {
    icon: ReactNode;
    tooltip: string;
}

export const AssistantMessageCopyButton = ({ conversationId, message: { id, content } }: Props) => {
    const { tShared } = useSharedTranslation();
    const { trackEvent } = useTracking();
    const [copyStatus, setCopyStatus] = useState<CopyStatus>("none");

    const copyToClipboard = useCallback(async () => {
        if (copyStatus !== "none") {
            return;
        }

        try {
            await navigator.clipboard.writeText(content);

            trackEvent(MixpanelEvent.AssistantResponseCopied, {
                "Conversation Id": conversationId,
                "Message Id": id,
                Type: MixpanelCopyResponseType.CopyButton
            });
            setCopyStatus("success");
        } catch {
            setCopyStatus("error");
        } finally {
            setTimeout(() => (setCopyStatus("none")), 2000);
        }
    }, [content, conversationId, copyStatus, id, trackEvent]);

    const copyStatusContent: Record<CopyStatus, CopyStatusContent> = useMemo(() => ({
        none: {
            icon: <IconDuplicate />,
            tooltip: tShared("copy")
        },
        success: {
            icon: <IconCheck />,
            tooltip: tShared("copied")
        },
        error:{
            icon: <IconX />,
            tooltip: tShared("failed")
        }
    }), [tShared]);

    const { icon, tooltip } = copyStatusContent[copyStatus];

    return (
        <AssistantMessageToolbarButton
            aria-label={tooltip}
            tooltip={tooltip}
            onPress={copyToClipboard}
        >
            {icon}
        </AssistantMessageToolbarButton>
    );
};

